import React, { useContext, useEffect, useRef, useState } from 'react'
import dayjs from 'dayjs'
import { useHistory, useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { css } from '@emotion/core'
import { AccountContext } from '@/contexts/account'
import { GuestRoomAssignResponseType, PaxFullObject } from '@/models/guest-room/guest-room-assign'
import { AdminReservationResponseType } from '@/models/reservation/admin-reservation'
import { useHotelGuestRooms } from '@/hooks/use-hotel-guest-rooms'
import { useRoomsSelectable } from '@/hooks/use-rooms-selectable'
import { useEditGuestRooms } from '@/hooks/use-edit-guest-rooms'
import { setHasRoomManagerPlugin, setHasSmartKeyPlugin, setHasPmsCardKeyPlugin, setHasMiwaLockPlugin } from '@/libs/plugins'
import { validUpdateGuestRoom } from '@/components/organisms/customer/_guest-room/valid-update-guest-room'
import { useErrorHandler } from '@/hooks/use-error-handler'
import { NotEditFormItem } from '@/components/organisms/customer/_guest-room/not-edit-form-item'
import { AddRoomButton } from '@/components/organisms/customer/_guest-room/add-room-button'
import { EditRoomButton } from '@/components/organisms/customer/_guest-room/edit-room-button'
import { Button } from '@/components/atoms/button'
import { updateGuestRoomAssign, updateAllGuestRoomAssign, guestRoomFix, guestRoomAutoAssign } from '@/apis/aipass'
import { MiwaLockStatus } from '@/constants/miwa-lock'
import { KeyCardWrite, KeyCardWriteHandle } from './miwa-lock/key-card-write'
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore'
import { simpleTableStyle } from '@/constants/layout'
import { RegistrationGuestNameButton } from '../accommodation-management/registraton-guest-name-button'
import { Hint } from '@/components/atoms/hint'
import { FreeEditFormItem } from './_guest-room/free-edit-form-item'
dayjs.extend(isSameOrBefore)

type GuestRoomProps = {
  reservation: AdminReservationResponseType['reservations']
  setIsLoading: React.Dispatch<React.SetStateAction<boolean>>
  onSavedData: () => void
}

export const GuestRoom: React.FC<GuestRoomProps> = ({ reservation, setIsLoading, onSavedData }) => {
  const { plugins, account } = useContext<any>(AccountContext)
  const { t } = useTranslation()
  const { errorHandler } = useErrorHandler()
  const history = useHistory()
  const location = useLocation<any>()

  // Room list to edit
  const [editGuestRooms, setEditGuestRooms, handleFetchGuestRooms] = useEditGuestRooms()
  // Hotel room list
  const [hotelGuestRooms, handleFetchGuestRoomForHotel] = useHotelGuestRooms()
  // List of selectable rooms
  const [roomsSelectable, handleFetchSelectableGuestRoom] = useRoomsSelectable()
  // Show/hide save button
  const [isEdit, setIsEdit] = useState<boolean>(false)
  const [showAutomaticAssignmentModal, setShowAutomaticAssignmentModal] = useState<boolean>(false)
  // Assignment Room Removed
  const [deletedEditGuestRooms, setDeletedEditGuestRooms] = useState<Array<{ guestRoomAssignId: string }>>([])

  /* Plugin judgment */
  // Whether it is a room management plugin
  const hasRoomManagerPlugin = setHasRoomManagerPlugin(plugins)
  // Check has plugin Smart Key
  const hasSmartKeyPlugin = setHasSmartKeyPlugin(plugins)
  // 対面発行は PMS_CARD_KEY が主体。発行ボタンは美和カードの発行処理のため、美和ロックの契約も条件にする
  const canIssueMiwaCardKey = setHasPmsCardKeyPlugin(plugins) && setHasMiwaLockPlugin(plugins)

  const keyCardWriteRef = useRef<KeyCardWriteHandle>(null)
  const [isCurrentDateSameOrBeforeCheckoutDate, setIsCurrentDateSameOrBeforeCheckoutDate] = useState(false)
  const canBeAutoAssignment = editGuestRooms?.length && editGuestRooms.some(i => i.roomNumber === '指定なし') && reservation?.reservationId
  const canBeFixed =
    editGuestRooms?.length && editGuestRooms.every(i => !!i.roomNumber && i.roomNumber !== '指定なし') && reservation?.reservationId
  const [isFixed, setIsFixed] = useState<boolean>(reservation?.isFixed || false)
  /**
   * Remote lock related variables
   */
  // RemoteLock plugin
  // const hasRemoteLockPlugin = setHasRemoteLockPlugin(plugins)
  // Current room assignment status
  // const [userRoomAssign, setUserRoomAssign] = useState<userRoomAssignType[]>()
  // RemoteLock client information
  // const [remoteLockClientInfo, setRemoteLockClientInfo] = useState<clientInfoType>()
  // Remote lock setting state management of setting information
  // const [isRemoteLockSelected, setIsRemoteLockSelected] = useState<boolean>()

  /**
   * Room assignment cancellation process
   * Revert to current assignment
   */
  const onClickCancel = async () => {
    setIsLoading(true)

    setIsEdit(false)
    await handleFetchGuestRooms({ reservationId: reservation?.reservationId })

    setIsLoading(false)
  }
  const _handleFetchGuestRooms = async () => {
    if (!hasRoomManagerPlugin) {
      handleFetchGuestRoomForHotel()
      handleFetchSelectableGuestRoom()
    }

    await handleFetchGuestRooms({ reservationId: reservation?.reservationId }).then(e => {})
  }

  useEffect(() => {
    if (!account?.hotel?.id) return
    if (reservation?.reservationId) {
      _handleFetchGuestRooms()
      checkCurrentDateSameOrBeforeCheckoutDate()
    }
    setIsFixed(reservation?.isFixed || false)
  }, [reservation, account?.hotel?.id])

  const onEditRoomAssignGuestName = (event: React.ChangeEvent<HTMLInputElement>, selectIndex: number) => {
    // Create a new array to update the state
    const _editGuestRooms = [...editGuestRooms]
    _editGuestRooms[selectIndex].assignGuestName = event.target.value
    _editGuestRooms[selectIndex].reservationGuestName = event.target.value
    setEditGuestRooms(_editGuestRooms)
    setIsEdit(true)
  }

  const onEditRoomAssignPax = (event: React.ChangeEvent<HTMLInputElement>, selectIndex: number) => {
    // Create a new array to update the state
    const _editGuestRooms = [...editGuestRooms]
    _editGuestRooms[selectIndex].pax = parseInt(event.target.value) || 0
    setEditGuestRooms(_editGuestRooms)
    setIsEdit(true)
  }

  const onEditPaxFull = async (paxObject: PaxFullObject, selectIndex: number) => {
    try {
      setIsLoading(true)

      const _editGuestRooms = [...editGuestRooms]

      _editGuestRooms[selectIndex] = { ..._editGuestRooms[selectIndex], ...paxObject }

      await _updateGuestRoomAssign(_editGuestRooms)

      setEditGuestRooms(_editGuestRooms)
      onSavedData()
    } catch (error) {
      errorHandler(error)
    } finally {
      setIsLoading(false)
    }
  }

  const _updateGuestRoomAssign = async editGuestRooms => {
    if (!hasRoomManagerPlugin) return await updateAllGuestRoomAssign(reservation, editGuestRooms)

    return await updateGuestRoomAssign(editGuestRooms)
  }

  /**
   * Add room button
   */
  const onFieldPush = () => {
    if (!reservation) return

    let _editGuestRooms: typeof editGuestRooms = []
    if (editGuestRooms.length) {
      _editGuestRooms = [...editGuestRooms]
    }

    _editGuestRooms.push({
      reservationId: reservation?.reservationId,
      assignGuestName: reservation?.guestName,
      reservationUserName: reservation?.userName,
      reservationGuestName: reservation?.guestName,
      /**
       * Check-in, check-out defaults to booking check-in, check-out
       * If there is no check-out date, set the day after the check-in date
       */
      assignCheckinDate: reservation.checkinDate,
      assignCheckoutDate: reservation.checkoutDate
        ? reservation.checkoutDate
        : dayjs(reservation.checkinDate).add(1, 'day').format('YYYY-MM-DD HH:mm:ss'),
      roomTypeId: null,
      typeNameJa: null,
      typeNameEn: null,
      guestRoomId: null,
      roomNumber: null,
      keyNumber: null,
      issueType: '',
    })

    setIsEdit(true)
    setEditGuestRooms(_editGuestRooms)
  }

  /**
   * save button
   */
  const updateGuestRoom = async () => {
    try {
      setIsLoading(true)

      // validation
      if (!validUpdateGuestRoom(editGuestRooms)) {
        setIsLoading(false)
        return
      }

      let _editGuestRooms = [...editGuestRooms]

      // Add to editGuestRooms if there was a deleted assigned room
      if (deletedEditGuestRooms.length) {
        _editGuestRooms = [...editGuestRooms, ...deletedEditGuestRooms]
      }

      await _updateGuestRoomAssign(_editGuestRooms).then(() => {
        setIsEdit(false)
        onSavedData()
      })
    } catch (error) {
      errorHandler(error)
    } finally {
      setIsLoading(false)
    }
  }

  /**
   * Returns the manually set key number or access guest PIN number depending on the key number acquisition processing status.
   */
  const getGuestRoomKeyNumber = (room: GuestRoomAssignResponseType['rooms'][0] | null): string => {
    if (!room) return '-'
    /**
     * remote lock plug-in off
     */
    return room.keyNumber ?? '-'
  }

  const checkCurrentDateSameOrBeforeCheckoutDate = () => {
    const currentDate = dayjs().format('YYYY-MM-DD')
    const reservationCheckoutDate = dayjs(reservation.checkoutDate).format('YYYY-MM-DD')
    const isSameOrBeforeDate = dayjs(currentDate).isSameOrBefore(reservationCheckoutDate)
    setIsCurrentDateSameOrBeforeCheckoutDate(isSameOrBeforeDate)
  }

  const miwaButtonType = (issueType: string) => {
    if (isEdit || !isCurrentDateSameOrBeforeCheckoutDate) {
      return 5 // Disable button when editing
    }

    if (issueType === MiwaLockStatus.ISSUE) {
      return 7
    }

    if (issueType === MiwaLockStatus.REISSUE) {
      return 4
    }

    return 3
  }

  const fixedRooms = async () => {
    try {
      setIsLoading(true)

      await guestRoomFix({ reservationId: reservation.reservationId, isFixed: !isFixed })

      setIsFixed(!isFixed)

      await _handleFetchGuestRooms()
    } catch (error) {
      errorHandler(error)
    } finally {
      setIsLoading(false)
    }
  }

  const makeAutomaticAssignment = async () => {
    try {
      setShowAutomaticAssignmentModal(false)
      setIsLoading(true)

      const res = await guestRoomAutoAssign({ reservationIds: [reservation.reservationId], pageType: 'reservation' })

      if (res?.errors) errorHandler({ response: { data: res } })
      onSavedData()
      await _handleFetchGuestRooms()
    } catch (error) {
      errorHandler(error)
    } finally {
      setIsLoading(false)
    }
  }

  const onChangeRoomStatus = async (): Promise<void> => {
    await handleFetchGuestRooms({ reservationId: reservation?.reservationId })
    onSavedData()
  }

  return (
    <div css={guestRoomWrapperStyle}>
      <div
        css={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '6.5px 32px',
          '@media(max-width: 1080px)': {
            paddingInline: 16,
          },
        }}
      >
        <div css={{ display: 'flex', alignItems: 'center' }}>
          <div css={[guestRoomTitleStyle, { marginRight: 32 }]}>{t('Guest room')}</div>
          {!!canBeAutoAssignment && (
            <Button
              width={120}
              height={24}
              buttonType={2}
              fontSize={12}
              borderRadius={4}
              onClick={() => setShowAutomaticAssignmentModal(true)}
            >
              {t('Automatic assignment')}
            </Button>
          )}
          {showAutomaticAssignmentModal && (
            <div
              css={{
                position: 'fixed',
                top: 0,
                left: 0,
                bottom: 0,
                right: 0,
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                backgroundColor: 'rgba(0,0,0,0.5)',
                zIndex: 3,
              }}
            >
              <div css={{ backgroundColor: '#fff', padding: 32, borderRadius: 8 }}>
                <div css={{ fontWeight: 'bold', marginBottom: 32, fontSize: 18 }}>{t('Are you sure you want to assign?')}</div>
                <div css={{ display: 'flex', justifyContent: 'space-around', alignItems: 'center' }}>
                  <Button width={116} height={36} buttonType={3} onClick={() => setShowAutomaticAssignmentModal(false)}>
                    {t('Cancel')}
                  </Button>
                  <Button width={116} height={36} buttonType={2} onClick={makeAutomaticAssignment}>
                    {t('Assignment')}
                  </Button>
                </div>
              </div>
            </div>
          )}
        </div>
        {!!editGuestRooms?.length && (
          <div css={{ display: 'flex', gap: 24 }}>
            {reservation?.reservationId && (
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <Hint customCss={hintImageStyle}>
                  <div className="inner-block">
                    <p>「部屋番号」「部屋代表者」の列名を含むCSVファイルをアップロードしてください。</p>
                    <img src={require('@/static/images/room/説明画像.png')} alt="" />
                  </div>
                </Hint>
                <RegistrationGuestNameButton
                  reservationRooms={editGuestRooms}
                  onReload={() => {
                    handleFetchGuestRooms({ reservationId: reservation.reservationId })
                  }}
                />
              </div>
            )}
            <div css={[fixCheckStyle, { cursor: canBeFixed ? 'pointer' : 'default' }]} onClick={() => canBeFixed && fixedRooms()}>
              <img
                src={require(`@/static/images/room/check-box${isFixed ? '_yellow_on' : '_off'}.svg`)}
                css={{ backgroundColor: !canBeFixed ? '#f2f2f2' : undefined }}
              />
              <p css={{ color: !canBeFixed ? '#ccc !important' : undefined, fontWeight: 'bold' }}>{t('Fixed room')}</p>
            </div>
          </div>
        )}
      </div>
      <table
        css={[
          simpleTableStyle,
          css({
            'th:last-of-type, td:last-of-type': { paddingLeft: 24 },
          }),
        ]}
      >
        <thead>
          <tr>
            <th css={{ width: 81, '@media(max-width: 1080px)': { width: 65 } }}>{t('Room ID')}</th>
            <th css={{ width: 170 }}>{t('Date')}</th>
            <th>{t('Room representative')}</th>
            <th css={{ width: 80 }}>{t('Capacity')}</th>
            <th>{t('Room type')}</th>
            <th css={{ minWidth: 70 }}>{t('Room number')}</th>
            <th css={{ width: 130 }}>{t('Status')}</th>
            <th css={{ width: 136, '@media(max-width: 1080px)': { width: 120 } }}>{t('Smart key')}</th>
          </tr>
        </thead>
        <tbody>
          {hasRoomManagerPlugin && reservation ? <></> : <></>}

          {reservation &&
            (hasRoomManagerPlugin ? (
              <>
                {editGuestRooms?.map((room, index) => {
                  return (
                    <NotEditFormItem
                      reservation={reservation}
                      room={room}
                      roomIndex={index}
                      key={index}
                      miwaButtonType={miwaButtonType}
                      hasSmartKeyPlugin={hasSmartKeyPlugin}
                      canIssueMiwaCardKey={canIssueMiwaCardKey}
                      onOpenKeyCardWriteModal={room => keyCardWriteRef.current?.open(room)}
                      getGuestRoomKeyNumber={getGuestRoomKeyNumber}
                      onEditRoomAssignGuestName={onEditRoomAssignGuestName}
                      onEditRoomAssignPax={onEditRoomAssignPax}
                      onEditPaxFull={onEditPaxFull}
                      isCurrentDateSameOrBeforeCheckoutDate={isCurrentDateSameOrBeforeCheckoutDate}
                      onSavedData={onChangeRoomStatus}
                    />
                  )
                })}
                {isEdit && (
                  <tr>
                    <td colSpan={8} css={{ padding: '0 !important' }}>
                      {<EditRoomButton updateGuestRoom={updateGuestRoom} onClickCancel={onClickCancel} />}
                    </td>
                  </tr>
                )}
                {!isEdit && !editGuestRooms.length && (
                  <tr>
                    <td colSpan={8} css={{ padding: '0 !important' }}>
                      <div
                        css={addRoomListWrapperOpenStyle}
                        onClick={() =>
                          history.push({
                            pathname: '/room-manager/daily',
                            search: `reservationId=${reservation.reservationId}`,
                            state: {
                              ...location.state,
                            },
                          })
                        }
                      >
                        <img src={require('@/static/images/plus.svg')} css={addIconStyle} alt={t('Add icon yellow')} />
                        <p>{t('Add room')}</p>
                      </div>
                    </td>
                  </tr>
                )}
              </>
            ) : (
              <>
                {editGuestRooms?.map((room, index) => {
                  return (
                    <FreeEditFormItem
                      key={index}
                      room={room}
                      roomIndex={index}
                      miwaButtonType={miwaButtonType}
                      hasSmartKeyPlugin={hasSmartKeyPlugin}
                      canIssueMiwaCardKey={canIssueMiwaCardKey}
                      onOpenKeyCardWriteModal={room => keyCardWriteRef.current?.open(room)}
                      getGuestRoomKeyNumber={getGuestRoomKeyNumber}
                      onEditRoomAssignGuestName={onEditRoomAssignGuestName}
                      onEditRoomAssignPax={onEditRoomAssignPax}
                      onEditPaxFull={onEditPaxFull}
                      isCurrentDateSameOrBeforeCheckoutDate={isCurrentDateSameOrBeforeCheckoutDate}
                      isEdit={isEdit}
                      setIsEdit={setIsEdit}
                      editGuestRooms={editGuestRooms}
                      setEditGuestRooms={setEditGuestRooms}
                      deletedEditGuestRooms={deletedEditGuestRooms}
                      setDeletedEditGuestRooms={setDeletedEditGuestRooms}
                      hotelGuestRooms={hotelGuestRooms}
                      roomsSelectable={roomsSelectable}
                      onSavedData={onChangeRoomStatus}
                    />
                  )
                })}
                <tr>
                  <td colSpan={8} css={{ padding: '0 !important' }}>
                    <AddRoomButton
                      onFieldPush={onFieldPush}
                      updateGuestRoom={updateGuestRoom}
                      onClickCancel={onClickCancel}
                      isEdit={isEdit}
                    />
                  </td>
                </tr>
              </>
            ))}
        </tbody>
      </table>

      <KeyCardWrite ref={keyCardWriteRef} reservation={reservation} onWritten={_handleFetchGuestRooms} />
    </div>
  )
}

// guest room
const guestRoomWrapperStyle = css({
  border: '1px solid #CCCCCC',
  background: '#FFF',
  borderRadius: 10,
})

const guestRoomTitleStyle = css({
  fontSize: 14,
  fontWeight: 'bold',
})

const addRoomListWrapperOpenStyle = css({
  cursor: 'pointer',
  display: 'flex',
  height: 36,
  backgroundColor: '#FAFAFA',
  justifyContent: 'center',
  alignItems: 'center',
  border: '1px dashed #CCCCCC',
  margin: '24px 32px',
  p: {
    lineHeight: '36px',
    color: '#F2A40B',
    fontSize: 12,
    fontWeight: 'bold',
    paddingLeft: 8,
  },
  marginTop: 24,
})

const addIconStyle = css({
  width: 13,
  height: 13,
})

const fixCheckStyle = css({
  display: 'flex',
  alignItems: 'center',
  cursor: 'pointer',
  img: {
    marginRight: 8,
  },
  p: {
    fontSize: 12,
    letterSpacing: 0.6,
    color: '#676767',
  },
})

const hintImageStyle = css({
  '.help-icon': { width: 18, height: 18 },
  '.help-popup': { right: 26, top: -13 },
  '.inner-block': {
    width: 386,
    padding: 16,
    textAlign: 'center',
    p: {
      marginBottom: 16,
      color: '#676767',
      fontWeight: 'bold',
      textAlign: 'left',
      lineHeight: 1.5,
      letterSpacing: 1.2,
      fontSize: 12,
    },
    img: { width: 181 },
  },
})
